import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { UntypedFormControl } from '@angular/forms'
import { dateValidator } from '@ftr/forms'
import { DestroySubscribers } from '@ftr/foundation'
import moment from 'moment-timezone'
import { Observable, Subject, distinctUntilChanged, skip, takeUntil, tap } from 'rxjs'
import { ComplexFilterData } from '../ComplexFilter'
import { DateFilter } from '../DateFilter'
import { ComplexFilterService } from '../complex-filter.service'
import { COMPLEX_FILTER_DATE_FORMAT, getComplexDateFilterFormattedDate } from './ComplexDateFilterUtils'

@Component({
  selector: 'ftr-complex-filter-date',
  templateUrl: './complex-filter-date.component.html',
  styleUrls: ['./complex-filter-date.component.css'],
})
export class ComplexFilterDateComponent extends DestroySubscribers implements OnInit {
  @Input() filter: DateFilter
  @Input() visibilityToggled: Observable<boolean>
  @Output() filterChanged = new EventEmitter<ComplexFilterData>()

  readonly dateFormat = COMPLEX_FILTER_DATE_FORMAT

  clearDateSubject = new Subject<void>()

  internalFormControl = new UntypedFormControl(undefined)
  dateControl = new UntypedFormControl(undefined, [dateValidator])

  private lastValue: string | undefined

  constructor(private readonly complexFilterService: ComplexFilterService) {
    super()
  }

  ngOnInit(): void {
    this.buildInternalFormControls()
    this.listenToFilterChange()
    this.listenToVisibilityChange()
  }

  listenToFilterChange(): void {
    this.complexFilterService
      .listenToFilterChange([this.filter])
      .pipe(takeUntil(this.finalize))
      .subscribe(filters => {
        const date = filters[this.filter.filterKey] as string | undefined

        this.updateControls(date, true)
      })
  }

  /**
   * This is to ensure the value of the filter resets to the previous one
   * when we change it manually and close the modal without applying.
   */
  private listenToVisibilityChange(): void {
    this.visibilityToggled
      .pipe(
        takeUntil(this.finalize),
        tap(visible => {
          if (!visible) {
            this.updateControls(this.lastValue)
          }
        }),
      )
      .subscribe()
  }

  private buildInternalFormControls(): void {
    this.dateControl.valueChanges
      .pipe(
        takeUntil(this.finalize),
        distinctUntilChanged(momentDateOrUndefinedComparator),
        // Date picker component calls blur when opened, we skip to avoid extra control updates
        skip(1),
      )
      .subscribe(date => {
        this.filterChanged.emit({
          [this.filter.filterKey]: date ? getComplexDateFilterFormattedDate(date) : undefined,
        })
      })
  }

  private updateControls(date: string | undefined, updateLastValue = false): void {
    this.internalFormControl.patchValue(date, { emitEvent: false })
    if (date) {
      this.dateControl.patchValue(moment(date, COMPLEX_FILTER_DATE_FORMAT))
    } else {
      this.clearDateSubject.next()
    }

    if (updateLastValue) {
      this.lastValue = this.internalFormControl.value
    }
  }
}

function momentDateOrUndefinedComparator(
  previous: moment.Moment | undefined,
  current: moment.Moment | undefined,
): boolean {
  return (moment.isMoment(previous) && moment.isMoment(current) && previous.isSame(current)) || previous === current
}
